import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn, Loader2, Home, Phone, KeyRound, ShieldCheck, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api, setAuth, roleHome, getActiveSubdomain } from "@/lib/api";

export default function TenantLogin() {
  const { subdomain: paramSubdomain } = useParams();
  const subdomain = getActiveSubdomain() || paramSubdomain;
  const navigate = useNavigate();
  const site = useOutletContext();

  // ── Login state ──
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginRole, setLoginRole] = useState("customer");

  // ── Forgot password state ──
  // mode: 'login' | 'forgot_step1' | 'forgot_step2' | 'forgot_step3' | 'done'
  const [mode, setMode] = useState("login");
  const [fpMobile, setFpMobile] = useState("");
  const [fpOtp, setFpOtp] = useState("");
  const [fpNewPw, setFpNewPw] = useState("");
  const [fpConfirmPw, setFpConfirmPw] = useState("");
  const [showNewPw, setShowNewPw] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [otpMsg, setOtpMsg] = useState("");

  const resetForgot = () => {
    setMode("login");
    setFpMobile(""); setFpOtp("");
    setFpNewPw(""); setFpConfirmPw("");
    setOtpMsg(""); setShowNewPw(false);
  };

  // ── Login submit ──
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/tenant-login", {
        login: mobile,
        password,
        subdomain,
        role: loginRole,
      });
      setAuth(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate(roleHome(data.user.role, subdomain), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  // ── Step 1: Send OTP ──
  const sendOtp = async (e) => {
    e.preventDefault();
    if (!fpMobile.trim()) { toast.error("Enter your mobile number"); return; }
    setFpLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", {
        mobile: fpMobile.trim(),
        subdomain,
        role: loginRole,
      });
      setOtpMsg(data.message);
      setMode("forgot_step2");
      toast.success("OTP sent!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally { setFpLoading(false); }
  };

  // ── Step 2: Verify OTP ──
  const verifyOtp = async (e) => {
    e.preventDefault();
    if (!fpOtp.trim()) { toast.error("Enter the OTP"); return; }
    // We don't pre-verify — just move to step 3
    setMode("forgot_step3");
  };

  // ── Step 3: Set new password ──
  const resetPassword = async (e) => {
    e.preventDefault();
    if (fpNewPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (fpNewPw !== fpConfirmPw) { toast.error("Passwords do not match"); return; }
    setFpLoading(true);
    try {
      await api.post("/auth/reset-password", {
        mobile: fpMobile.trim(),
        otp: fpOtp.trim(),
        new_password: fpNewPw,
        subdomain,
      });
      setMode("done");
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed. Check your OTP.");
      setMode("forgot_step2"); // go back to OTP step on failure
    } finally { setFpLoading(false); }
  };

  if (!site) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <h1 className="font-heading font-bold text-2xl text-slate-700">Site Not Found</h1>
        <p className="mt-2 text-slate-500">The requested operator portal does not exist.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 font-body">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <Link to={getActiveSubdomain() ? "/" : `/site/${subdomain}`} className="inline-flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold bg-brand-primary">
            {site.brand_name ? site.brand_name[0].toUpperCase() : "D"}
          </div>
          <span className="font-heading font-bold text-2xl text-slate-900">{site.brand_name || "DTH Portal"}</span>
        </Link>

        {mode === "login" && <h2 className="mt-2 text-3xl font-heading font-bold text-slate-900">Sign in to your account</h2>}
        {mode === "forgot_step1" && <h2 className="mt-2 text-3xl font-heading font-bold text-slate-900">Forgot Password</h2>}
        {mode === "forgot_step2" && <h2 className="mt-2 text-3xl font-heading font-bold text-slate-900">Enter OTP</h2>}
        {mode === "forgot_step3" && <h2 className="mt-2 text-3xl font-heading font-bold text-slate-900">Set New Password</h2>}
        {mode === "done"         && <h2 className="mt-2 text-3xl font-heading font-bold text-slate-900">Password Reset!</h2>}

        <p className="mt-2 text-sm text-slate-500">
          {mode === "login"         && "Access your dashboard"}
          {mode === "forgot_step1"  && "Enter your registered mobile number"}
          {mode === "forgot_step2"  && `OTP sent to ****${fpMobile.slice(-4)}`}
          {mode === "forgot_step3"  && "Choose a strong new password"}
          {mode === "done"          && "You can now sign in with your new password"}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-2xl sm:px-10 border border-slate-100">

          {/* ── Step indicator (forgot flow) ── */}
          {["forgot_step1","forgot_step2","forgot_step3"].includes(mode) && (
            <div className="flex items-center gap-2 mb-6">
              {[1,2,3].map((step, i) => {
                const current = { forgot_step1:1, forgot_step2:2, forgot_step3:3 }[mode];
                return (
                  <React.Fragment key={step}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      step < current ? "bg-green-500 text-white" :
                      step === current ? "bg-brand-primary text-white" :
                      "bg-slate-100 text-slate-400"
                    }`}>
                      {step < current ? <CheckCircle2 className="w-4 h-4" /> : step}
                    </div>
                    {i < 2 && <div className={`flex-1 h-0.5 transition-all ${step < current ? "bg-green-400" : "bg-slate-200"}`} />}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {/* ── LOGIN FORM ── */}
          {mode === "login" && (
            <form className="space-y-6" onSubmit={submit}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Login As</label>
                <div className="flex bg-slate-100 p-1 rounded-lg">
                  {['customer', 'dealer', 'agent', 'admin'].map(r => (
                    <button key={r} type="button" onClick={() => setLoginRole(r)}
                      className={`flex-1 py-1 text-sm font-semibold rounded-md transition-all capitalize ${
                        loginRole === r ? 'bg-white text-brand-primary shadow-sm' : 'text-slate-500 hover:text-slate-700'
                      }`}>
                      {r === 'dealer' ? 'Dealer/Staff' : r}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Mobile Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input value={mobile} onChange={(e) => setMobile(e.target.value)} type="tel" required
                    className="w-full pl-10 border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                    placeholder="Enter your mobile number" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-slate-700">Password</label>
                  <button type="button" onClick={() => { setFpMobile(mobile); setMode("forgot_step1"); }}
                    className="text-xs font-semibold text-brand-primary hover:underline">
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <input value={password} onChange={(e) => setPassword(e.target.value)} type={showPw ? "text" : "password"} required
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                    placeholder="••••••••" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={loading}
                className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                Sign in
              </button>
            </form>
          )}

          {/* ── STEP 1: Enter mobile ── */}
          {mode === "forgot_step1" && (
            <form className="space-y-5" onSubmit={sendOtp}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Registered Mobile Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input value={fpMobile} onChange={e => setFpMobile(e.target.value)} type="tel" required autoFocus
                    className="w-full pl-10 border border-slate-200 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                    placeholder="Enter your mobile number" />
                </div>
              </div>
              <button type="submit" disabled={fpLoading}
                className="w-full flex justify-center items-center gap-2 py-3 rounded-lg text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all disabled:opacity-60">
                {fpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Send OTP
              </button>
              <button type="button" onClick={resetForgot}
                className="w-full flex items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-700 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to Login
              </button>
            </form>
          )}

          {/* ── STEP 2: Enter OTP ── */}
          {mode === "forgot_step2" && (
            <form className="space-y-5" onSubmit={verifyOtp}>
              {otpMsg && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-xs text-blue-700 font-medium">{otpMsg}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Enter OTP</label>
                <input value={fpOtp} onChange={e => setFpOtp(e.target.value)} type="text"
                  inputMode="numeric" maxLength={6} required autoFocus
                  className="w-full text-center text-2xl font-heading font-bold tracking-[0.5em] border border-slate-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                  placeholder="· · · ·" />
                <p className="text-[11px] text-slate-400 mt-1.5 text-center">Enter the 4-digit OTP sent to your mobile</p>
              </div>
              <button type="submit"
                className="w-full flex justify-center items-center gap-2 py-3 rounded-lg text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all">
                <KeyRound className="w-4 h-4" /> Verify OTP
              </button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => setMode("forgot_step1")} className="text-slate-500 hover:text-slate-700 flex items-center gap-1">
                  <ArrowLeft className="w-3 h-3" /> Change number
                </button>
                <button type="button" onClick={sendOtp} disabled={fpLoading} className="text-brand-primary font-semibold hover:underline disabled:opacity-50">
                  {fpLoading ? "Sending..." : "Resend OTP"}
                </button>
              </div>
            </form>
          )}

          {/* ── STEP 3: New password ── */}
          {mode === "forgot_step3" && (
            <form className="space-y-5" onSubmit={resetPassword}>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
                <div className="relative">
                  <input value={fpNewPw} onChange={e => setFpNewPw(e.target.value)} type={showNewPw ? "text" : "password"}
                    required autoFocus minLength={6}
                    className="w-full border border-slate-200 rounded-lg px-4 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent transition-all"
                    placeholder="At least 6 characters" />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm New Password</label>
                <input value={fpConfirmPw} onChange={e => setFpConfirmPw(e.target.value)} type="password" required
                  className={`w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 transition-all ${
                    fpConfirmPw && fpConfirmPw !== fpNewPw
                      ? "border-red-300 focus:ring-red-300"
                      : "border-slate-200 focus:ring-brand-primary focus:border-transparent"
                  }`}
                  placeholder="Re-enter new password" />
                {fpConfirmPw && fpConfirmPw !== fpNewPw && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
              <button type="submit" disabled={fpLoading || (fpConfirmPw && fpConfirmPw !== fpNewPw)}
                className="w-full flex justify-center items-center gap-2 py-3 rounded-lg text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all disabled:opacity-60">
                {fpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Reset Password
              </button>
            </form>
          )}

          {/* ── DONE ── */}
          {mode === "done" && (
            <div className="text-center space-y-5">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-9 h-9 text-green-500" />
              </div>
              <div>
                <p className="font-semibold text-slate-700">Your password has been reset.</p>
                <p className="text-sm text-slate-500 mt-1">Use your new password to sign in below.</p>
              </div>
              <button onClick={resetForgot}
                className="w-full flex justify-center items-center gap-2 py-3 rounded-lg text-sm font-bold text-white bg-brand-primary hover:bg-brand-primaryHover transition-all">
                <LogIn className="w-4 h-4" /> Back to Sign In
              </button>
            </div>
          )}

          {/* ── Bottom nav ── */}
          {mode === "login" && (
            <div className="mt-6 text-center">
              <Link to={getActiveSubdomain() ? "/" : `/site/${subdomain}`} className="text-sm font-medium text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                <Home className="w-4 h-4" /> Back to Home
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
